import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import CaseWorkspace, {
  CaseFacts,
  CaseNavigation,
} from "@/components/case-workspace";
import { FormContent, FormSection } from "@/components/page-canvas";
import { PasswordInput } from "@/components/forms/password-input";
import { errorText, FormErrors } from "@/components/forms/form-errors";
import SettingsLayout from "@/layouts/settings_layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FacebookSetup, type FacebookSetupProps } from "./facebook-setup";
import { InstagramSetup, type InstagramSetupProps } from "./instagram-setup";
import { WhatsappSetup, type WhatsappSetupProps } from "./whatsapp-setup";
import { userIntegrationsPath, userIntegrationPath } from "@/routes";

type ProviderId =
  | "stripe"
  | "smartbill"
  | "easybill_ro"
  | "ro_efactura"
  | "facebook"
  | "instagram"
  | "whatsapp";
type Integration = {
  id: ProviderId;
  name: string;
  category: string;
  documentation: string;
  available: boolean;
  ready: boolean;
  state: string;
  verified_at: string | null;
  fields: { name: string; label: string }[];
  series: string[];
  selected_series: string | null;
};
type Props = {
  integrations: Integration[];
  facebook: FacebookSetupProps;
  instagram: InstagramSetupProps;
  whatsapp: WhatsappSetupProps;
  invoice_default: string;
  test_connections: boolean;
  pending_invoices: number;
  section: "catalogue" | "details" | "setup";
  selected_provider_id: ProviderId | null;
};

export default function Integrations({
  integrations,
  facebook,
  instagram,
  whatsapp,
  invoice_default,
  test_connections,
  pending_invoices,
  section,
  selected_provider_id,
}: Props) {
  const { t, i18n } = useTranslation();
  const { errors } = usePage().props;
  const entry = integrations.find((item) => item.id === selected_provider_id);
  const social = (id: ProviderId) =>
    id === "facebook" || id === "instagram" || id === "whatsapp";
  const available = (item: Integration) =>
    item.id === "facebook"
      ? facebook.available
      : item.id === "instagram"
        ? instagram.available
        : item.id === "whatsapp"
          ? whatsapp.available
          : item.available;
  const assets = (id: ProviderId) =>
    id === "facebook"
      ? facebook.pages
      : id === "instagram"
        ? instagram.accounts
        : id === "whatsapp"
          ? whatsapp.numbers
          : [];
  const state = (item: Integration) => {
    if (social(item.id)) {
      const connected = assets(item.id);
      if (connected.some((asset) => asset.ready))
        return t("integrations.states.ready");
      const pending =
        item.id === "facebook"
          ? facebook.authorization
          : item.id === "instagram"
            ? instagram.authorization
            : whatsapp.authorization || whatsapp.launch;
      if (pending) return t("product_ux_admin.integrations.setup_pending");
      return t(
        `integrations.states.${connected.some((asset) => asset.state !== "disconnected") ? "unavailable" : "disconnected"}`,
      );
    }
    return t(
      `integrations.states.${item.ready ? "ready" : item.state === "verified" ? "series" : item.state}`,
    );
  };
  const connectedAssets = entry
    ? assets(entry.id).filter((asset) => asset.state !== "disconnected")
    : [];
  const items = [
    {
      id: "catalogue",
      label: t("product_ux_admin.integrations.catalogue"),
      href: userIntegrationsPath(),
    },
    ...(entry
      ? [
          {
            id: "details",
            label: t("product_ux_admin.integrations.details"),
            href: userIntegrationsPath({
              provider: entry.id,
              section: "details",
            }),
          },
          {
            id: "setup",
            label: t("product_ux_admin.integrations.setup"),
            href: userIntegrationsPath({
              provider: entry.id,
              section: "setup",
            }),
          },
        ]
      : []),
  ];
  const errorTargets = entry
    ? {
        ...Object.fromEntries(
          entry.fields.map((field) => [
            field.name,
            `${entry.id}-${field.name}`,
          ]),
        ),
        selected_series: `${entry.id}-series`,
        location_id: `${entry.id}-intake-location`,
      }
    : {};
  return (
    <SettingsLayout>
      <Head
        title={
          entry
            ? `${entry.name} · ${t("integrations.title")}`
            : t("integrations.title")
        }
      />
      <CaseWorkspace
        fluid={!entry}
        editor={!!entry}
        context={
          <CaseFacts
            facts={[
              {
                label: t("integrations.title"),
                value: t("integrations.default", { provider: invoice_default }),
              },
              ...(entry
                ? [
                    {
                      label: t("product_ux_admin.integrations.provider"),
                      value: entry.name,
                    },
                    {
                      label: t("product_ux_admin.integrations.readiness"),
                      value: state(entry),
                    },
                  ]
                : []),
            ]}
          />
        }
        navigation={
          <CaseNavigation
            items={items}
            active={section}
            label={t("product_ux_admin.tasks")}
          />
        }
      >
        <div className="space-y-6">
          <header className="space-y-2">
            <h1 className="text-xl font-semibold">
              {entry ? entry.name : t("integrations.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t(
                `product_ux_admin.integrations.${section === "catalogue" ? "catalogue" : section}_help`,
              )}
            </p>
            {test_connections && (
              <Badge variant="outline">{t("integrations.test")}</Badge>
            )}
          </header>
          <FormErrors errors={errors} targets={errorTargets} />
          {pending_invoices > 0 && (
            <p role="status" className="rounded-lg border p-4 text-sm">
              {t("integrations.pending", { count: pending_invoices })}
            </p>
          )}
          {!entry && (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                {t("integrations.description")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("integrations.default_help")}
              </p>
              <div className="grid items-start gap-4 @min-[960px]/settings-workspace:grid-cols-2">
                {integrations.map((item) => (
                  <article
                    key={item.id}
                    className="min-w-0 space-y-4 rounded-lg border bg-card p-5"
                  >
                    <div className="space-y-1">
                      <h2 className="font-semibold">{item.name}</h2>
                      <p className="text-sm text-muted-foreground">
                        {t(`integrations.categories.${item.category}`)}
                      </p>
                    </div>
                    <p className="text-sm font-medium">{state(item)}</p>
                    {!available(item) && (
                      <p className="text-sm text-muted-foreground">
                        {t("integrations.setup_unavailable")}
                      </p>
                    )}
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={userIntegrationsPath({
                            provider: item.id,
                            section: "details",
                          })}
                        />
                      }
                    >
                      {t("product_ux_admin.integrations.view", {
                        provider: item.name,
                      })}
                    </Button>
                  </article>
                ))}
              </div>
            </div>
          )}
          {entry && section === "details" && (
            <FormContent>
              <FormSection title={t("product_ux_admin.integrations.readiness")}>
                <p className="font-medium">{state(entry)}</p>
                <p className="text-sm text-muted-foreground">
                  {t(`integrations.help.${entry.id}`)}
                </p>
                {entry.verified_at && (
                  <p className="text-sm text-muted-foreground">
                    {t("integrations.verified_on", {
                      date: new Intl.DateTimeFormat(i18n.language, {
                        dateStyle: "medium",
                        timeZone: "UTC",
                      }).format(new Date(entry.verified_at)),
                    })}
                  </p>
                )}
                {social(entry.id) && (
                  <p className="text-sm">
                    {connectedAssets.length
                      ? t("product_ux_admin.integrations.assets", {
                          count: connectedAssets.length,
                        })
                      : t("product_ux_admin.integrations.no_assets")}
                  </p>
                )}
                {!available(entry) && (
                  <p role="status" className="rounded-lg border p-4 text-sm">
                    {t("product_ux_admin.integrations.unavailable_help")}
                  </p>
                )}
                <a
                  className="inline-block text-sm underline underline-offset-4"
                  href={entry.documentation}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("integrations.guide", { provider: entry.name })}
                </a>
              </FormSection>
              <Button
                render={
                  <Link
                    href={userIntegrationsPath({
                      provider: entry.id,
                      section: "setup",
                    })}
                  />
                }
              >
                {t("access.configure_provider", { provider: entry.name })}
              </Button>
            </FormContent>
          )}
          {entry && section === "setup" && (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">
                {t("access.provider_help")}
              </p>
              {entry.id === "facebook" ? (
                <FacebookSetup setup={facebook} />
              ) : entry.id === "instagram" ? (
                <InstagramSetup setup={instagram} />
              ) : entry.id === "whatsapp" ? (
                <WhatsappSetup setup={whatsapp} />
              ) : (
                <FormContent>
                  {!entry.available && (
                    <p role="status" className="rounded-lg border p-4 text-sm">
                      {t("product_ux_admin.integrations.unavailable_help")}
                    </p>
                  )}
                  {entry.available && entry.fields.length > 0 && (
                    <Form
                      action={userIntegrationPath(entry.id)}
                      method="patch"
                      resetOnSuccess
                    >
                      {({ processing }) => (
                        <div className="space-y-4">
                          <input
                            type="hidden"
                            name="return_section"
                            value="setup"
                          />
                          {entry.fields.map((field) => (
                            <div className="space-y-2" key={field.name}>
                              <Label htmlFor={`${entry.id}-${field.name}`}>
                                {field.label}
                              </Label>
                              <PasswordInput
                                id={`${entry.id}-${field.name}`}
                                name={`integration[credentials][${field.name}]`}
                                autoComplete="off"
                                required
                                aria-invalid={!!errors[field.name]}
                                aria-describedby={
                                  errors[field.name]
                                    ? `${entry.id}-${field.name}-error`
                                    : undefined
                                }
                              />
                              {errors[field.name] && (
                                <p
                                  id={`${entry.id}-${field.name}-error`}
                                  className="text-sm text-destructive"
                                >
                                  {errorText(errors[field.name])}
                                </p>
                              )}
                            </div>
                          ))}
                          <Button type="submit" disabled={processing}>
                            {t("integrations.connect", {
                              provider: entry.name,
                            })}
                          </Button>
                        </div>
                      )}
                    </Form>
                  )}
                  {entry.available &&
                    entry.state !== "disconnected" &&
                    entry.series.length > 0 && (
                      <Form
                        action={userIntegrationPath(entry.id)}
                        method="patch"
                      >
                        {({ processing }) => (
                          <div className="space-y-3">
                            <input
                              type="hidden"
                              name="return_section"
                              value="setup"
                            />
                            <Label htmlFor={`${entry.id}-series`}>
                              {t("integrations.series")}
                            </Label>
                            <Select
                              name="integration[selected_series]"
                              defaultValue={entry.selected_series ?? undefined}
                            >
                              <SelectTrigger
                                id={`${entry.id}-series`}
                                className="w-full"
                                aria-invalid={!!errors.selected_series}
                              >
                                <SelectValue
                                  placeholder={t("integrations.choose_series")}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {entry.series.map((series) => (
                                  <SelectItem key={series} value={series}>
                                    {series}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.selected_series && (
                              <p className="text-sm text-destructive">
                                {errorText(errors.selected_series)}
                              </p>
                            )}
                            <Button type="submit" disabled={processing}>
                              {t("integrations.save_series")}
                            </Button>
                          </div>
                        )}
                      </Form>
                    )}
                  {entry.state !== "disconnected" && (
                    <FormSection
                      title={t("integrations.disconnect", {
                        provider: entry.name,
                      })}
                    >
                      <Form
                        action={userIntegrationPath(entry.id)}
                        method="delete"
                      >
                        {({ processing }) => (
                          <>
                            <input
                              type="hidden"
                              name="return_section"
                              value="setup"
                            />
                            <Button
                              type="submit"
                              variant="outline"
                              disabled={processing}
                            >
                              {t("integrations.disconnect", {
                                provider: entry.name,
                              })}
                            </Button>
                          </>
                        )}
                      </Form>
                    </FormSection>
                  )}
                </FormContent>
              )}
            </div>
          )}
          {entry && (
            <Button
              variant="outline"
              render={<Link href={userIntegrationsPath()} />}
            >
              {t("product_ux_admin.integrations.back_catalogue")}
            </Button>
          )}
        </div>
      </CaseWorkspace>
    </SettingsLayout>
  );
}
